import { Box, Checkbox, Stack, Typography } from '@mui/material';
import { ErrorMessage, useFormikContext, FieldArray } from 'formik';
import TextInput from '../../form/base/TextInput';
import { FlashDesignFormPayload } from '../../../types/flashbooks';
import { FLASH_DESIGN_SIZE_OPTIONS } from '../../../constants/flashbooks';
import {
  SIZE_TABLE_COLUMNS,
  sizeTableHeaderStyle,
  sizeTableRowLastStyle,
  sizeTableRowStyle,
  sizeTableShellStyle,
} from '../../../helpers/styles/flashbook';

const renderError = (msg: string) => (
  <Typography
    color="error.dark"
    variant="caption"
    sx={{ mt: 0.5, display: 'block' }}>
    {msg}
  </Typography>
);

export default function SizeConfigTable() {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<FlashDesignFormPayload>();

  const showArrayError =
    typeof errors.sizeConfigs === 'string' &&
    !!touched.sizeConfigs &&
    !!errors.sizeConfigs;

  return (
    <FieldArray name="sizeConfigs">
      {() => (
        <Stack sx={sizeTableShellStyle} data-formik-field="sizeConfigs">
          <Box
            display="grid"
            gridTemplateColumns={SIZE_TABLE_COLUMNS}
            gap={2}
            color="text.secondary"
            sx={sizeTableHeaderStyle}>
            <span />
            <Typography variant="caption" fontWeight={600}>
              Available size
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Duration (min)
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Estimate
            </Typography>
          </Box>
          {values.sizeConfigs.map((config, index) => {
            const sizeMeta = FLASH_DESIGN_SIZE_OPTIONS.find(
              opt => opt.value === config.size,
            );
            const isLast = index === values.sizeConfigs.length - 1;
            return (
              <Box
                key={config.size}
                display="grid"
                gridTemplateColumns={SIZE_TABLE_COLUMNS}
                gap={2}
                alignItems="start"
                sx={isLast ? sizeTableRowLastStyle : sizeTableRowStyle}>
                <Checkbox
                  sx={{ mt: 0.5 }}
                  checked={config.enabled}
                  onChange={(_, checked) =>
                    setFieldValue(`sizeConfigs.${index}.enabled`, checked)
                  }
                />
                <Stack pt={1}>
                  <Typography variant="h5" color="common.black">
                    {sizeMeta?.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                    mt="2px">
                    {sizeMeta?.description}
                  </Typography>
                </Stack>
                <Box>
                  <TextInput
                    name={`sizeConfigs.${index}.durationInMinutes`}
                    type="number"
                    placeholder="60"
                    disabled={!config.enabled}
                  />
                  <ErrorMessage
                    name={`sizeConfigs.${index}.durationInMinutes`}
                    render={renderError}
                  />
                </Box>
                <Box>
                  <TextInput
                    name={`sizeConfigs.${index}.estimate`}
                    placeholder="150"
                    disabled={!config.enabled}
                  />
                  <ErrorMessage
                    name={`sizeConfigs.${index}.estimate`}
                    render={renderError}
                  />
                </Box>
              </Box>
            );
          })}
          {showArrayError ? (
            <Typography
              color="error.dark"
              variant="caption"
              component="p"
              sx={{
                px: 2,
                py: 1,
                borderTop: '1px solid',
                borderColor: 'darkerLightGrey.main',
              }}>
              {errors.sizeConfigs as string}
            </Typography>
          ) : null}
        </Stack>
      )}
    </FieldArray>
  );
}
